import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AdminGuard } from './components/common/AdminGuard';
import { DashboardPage } from './pages/DashboardPage';
import { BrandPortalPage } from './pages/BrandPortalPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { useAuth } from './hooks/useAuth';

/**
 * Root landing decision for non-portal routes.
 *
 * - Super admins and article contributors keep their existing experience: the
 *   AdminGuard-wrapped admin dashboard (unchanged behavior).
 * - A pure brand member (has brandMemberships, but is NOT a super admin and NOT
 *   an article contributor) is routed to their brand portal. If they belong to
 *   multiple brands, the first membership wins for now.
 * - Everyone else (loading, signed-out, no access) falls through to AdminGuard,
 *   which renders the spinner / login / access-denied screens as before.
 */
function AdminRoot() {
  const { isAdmin, isContributor, brandMemberships } = useAuth();

  if (!isAdmin && !isContributor && brandMemberships.length > 0) {
    return (
      <Navigate to={`/brand-portal/${brandMemberships[0].brandId}`} replace />
    );
  }

  return (
    <AdminGuard>
      <FeatureFlagsProvider>
        <DashboardPage />
      </FeatureFlagsProvider>
    </AdminGuard>
  );
}

export function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          {/* Public: reachable while signed out so invitees can accept. Must be
              declared before the :brandId route (static segment wins regardless,
              but keep it explicit). */}
          <Route
            path="/brand-portal/accept-invite"
            element={<AcceptInvitePage />}
          />
          <Route path="/brand-portal/:brandId" element={<BrandPortalPage />} />
          <Route path="*" element={<AdminRoot />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}
